import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { getScrollEventTarget, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * A custom element that scrubs a <video>'s currentTime in step with scroll
 * position, so the tea-steeping clip plays forward/backward as the user
 * scrolls through the section instead of autoplaying.
 *
 * On desktop (≥990px) this theme scrolls `.page-wrapper`, not `window`/
 * `document` (which are pinned with `overflow: hidden`) — so the scroll
 * listener and IntersectionObserver root must come from the theme's own
 * `@theme/scroll-container` helpers, not `window`, or nothing ever fires.
 */
class TeaSteepHero extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#video = this.querySelector('.tea-steep-hero__video');
    if (!this.#video) return;

    // iOS Safari (and Chrome/Firefox on iOS, which share the same engine)
    // aggressively discards the decoded frame buffer of a video that has
    // never actually played, even while its currentTime keeps being set —
    // it shows the first frame, then blanks out. Briefly playing and
    // immediately pausing "engages" the decoder so iOS keeps it alive.
    this.#video.muted = true;
    const playPromise = this.#video.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(() => this.#video?.pause()).catch(() => this.#video?.pause());
    } else {
      this.#video.pause();
    }

    this.#video.addEventListener('seeked', this.#onSeeked);
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);

    if (this.#video.readyState >= 1) {
      this.#onMetadataLoaded();
    } else {
      this.#video.addEventListener('loadedmetadata', () => this.#onMetadataLoaded(), { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#stopTracking();
    this.#clearSeekPending();
    this.#video?.removeEventListener('seeked', this.#onSeeked);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
  }

  #onMetadataLoaded() {
    const video = this.#video;
    if (!video || !isFinite(video.duration)) return;

    this.#duration = video.duration;

    if (prefersReducedMotion()) {
      video.currentTime = Math.max(0, this.#duration - 0.05);
      return;
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry?.isIntersecting) {
          this.#startTracking();
        } else {
          this.#stopTracking();
        }
      },
      { threshold: 0, root: getIntersectionRoot() }
    );
    this.#intersectionObserver.observe(this);
  }

  /** @type {HTMLVideoElement | null} */
  #video = null;
  #duration = 0;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  #listening = false;
  #ticking = false;
  #seekPending = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #seekTimeout = null;
  #scrollListener = () => this.#requestUpdate();
  #onSeeked = () => {
    this.#clearSeekPending();
    // A newer scroll position may have arrived while the last seek was
    // still decoding — catch up to it now that the video is free again.
    this.#requestUpdate();
  };
  #handleBreakpointChange = () => {
    // The scroll container (and therefore the correct event target and
    // IntersectionObserver root) switches between `.page-wrapper` and the
    // document root at the 990px breakpoint — rebuild both to match.
    const wasListening = this.#listening;
    this.#stopTracking();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry?.isIntersecting) {
          this.#startTracking();
        } else {
          this.#stopTracking();
        }
      },
      { threshold: 0, root: getIntersectionRoot() }
    );
    this.#intersectionObserver.observe(this);
    if (wasListening) this.#requestUpdate();
  };

  #clearSeekPending() {
    this.#seekPending = false;
    if (this.#seekTimeout !== null) {
      clearTimeout(this.#seekTimeout);
      this.#seekTimeout = null;
    }
  }

  #startTracking() {
    if (this.#listening) return;
    this.#listening = true;
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#scrollListener, { passive: true });
    window.addEventListener('resize', this.#scrollListener, { passive: true });
    this.#requestUpdate();
  }

  #stopTracking() {
    if (!this.#listening) return;
    this.#listening = false;
    this.#scrollTarget?.removeEventListener('scroll', this.#scrollListener);
    this.#scrollTarget = null;
    window.removeEventListener('resize', this.#scrollListener);
  }

  #requestUpdate() {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#updateProgress();
      this.#ticking = false;
    });
  }

  #updateProgress() {
    if (!this.#video || !this.#duration) return;

    // Never issue a second seek while one is still decoding — the browser
    // will just drop it, and flooding currentTime is what causes both the
    // scroll jank and the "only see the first/last frame" symptom. Wait for
    // `seeked` to fire (see #onSeeked) and it'll catch up automatically.
    if (this.#seekPending) return;

    const rect = this.getBoundingClientRect();
    const scrollableDistance = this.offsetHeight - window.innerHeight;

    let progress = 1;
    if (scrollableDistance > 0) {
      progress = -rect.top / scrollableDistance;
    }
    progress = Math.min(1, Math.max(0, progress));

    const targetTime = progress * this.#duration;

    if (Math.abs(targetTime - this.#video.currentTime) < 1 / 48) return;

    this.#seekPending = true;
    this.#video.currentTime = targetTime;

    // Safety net: if `seeked` never fires (some browsers/devices are
    // unreliable here), don't stay stuck forever — release the lock and
    // let the next scroll/frame try again.
    this.#seekTimeout = setTimeout(() => {
      this.#clearSeekPending();
      this.#requestUpdate();
    }, 400);
  }
}

if (!customElements.get('tea-steep-hero')) {
  customElements.define('tea-steep-hero', TeaSteepHero);
}
